import { useEffect, useState, useCallback } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, Sparkles, Crown, Zap, Gift, ArrowLeft, Bell, Lock } from "lucide-react";
import { toast } from "sonner";

const PLAN_ICONS = {
  free: Sparkles,
  standard: Zap,
  pro: Crown,
  fr_starter: Sparkles,
  fr_business: Zap,
  fr_premium: Crown,
};

const PLAN_COLORS = {
  free: { border: "border-[#1A3322]/15", bg: "bg-white", accent: "text-[#4B5563]" },
  standard: { border: "border-[#C85A32]", bg: "bg-gradient-to-br from-white to-[#FFF7ED]", accent: "text-[#C85A32]" },
  pro: { border: "border-[#1A3322]", bg: "bg-gradient-to-br from-[#F5F5F0] to-white", accent: "text-[#1A3322]" },
  fr_starter: { border: "border-green-500/40", bg: "bg-white", accent: "text-green-700" },
  fr_business: { border: "border-blue-500/40", bg: "bg-white", accent: "text-blue-700" },
  fr_premium: { border: "border-purple-500/40", bg: "bg-white", accent: "text-purple-700" },
};

export default function SellerSubscription() {
  const { user, loading: authLoading } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(null); // plan slug being processed

  // ----- France waitlist modal state (declared before early returns to keep hook order stable) -----
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistPlan, setWaitlistPlan] = useState("");
  const [wlEmail, setWlEmail] = useState("");
  const [wlName, setWlName] = useState("");
  const [wlCompany, setWlCompany] = useState("");
  const [wlNote, setWlNote] = useState("");
  const [wlBusy, setWlBusy] = useState(false);

  const load = useCallback(async () => {
    const [p, m] = await Promise.all([
      api.get("/subscriptions/plans"),
      api.get("/subscriptions/mine"),
    ]);
    setPlans(p.data || []);
    setMine(m.data);
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  // Handle Stripe redirect back (extracted so the linter can track the dep)
  const stripeSessionId = params.get("session_id");
  useEffect(() => {
    if (!stripeSessionId) return;
    (async () => {
      try {
        await api.get(`/subscriptions/checkout/status/${stripeSessionId}`);
        toast.success(t("sub.activated"));
        load();
      } catch (e) { console.warn("Subscription status check failed:", e?.message); }
    })();
  }, [stripeSessionId, t, load]);

  const cancelledFlag = params.get("cancelled");
  useEffect(() => {
    if (cancelledFlag) toast.info(t("sub.cancelled"));
  }, [cancelledFlag, t]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/connexion"/>;
  if (user.role !== "seller" && user.role !== "admin") return <Navigate to="/"/>;

  const subscribe = async (slug) => {
    setBusy(slug);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        plan_slug: slug,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!confirm(t("sub.cancel_confirm"))) return;
    try {
      await api.post("/subscriptions/cancel");
      toast.success(t("sub.cancel_scheduled"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || t("common.error")); }
  };

  const currentSlug = mine?.plan_slug || "free";
  const globalPlans = plans.filter((p) => p.market !== "france");
  const francePlans = plans.filter((p) => p.market === "france");

  const openWaitlist = (planSlug = "") => {
    setWaitlistPlan(planSlug);
    setWlEmail(user?.email || "");
    setWlName(user?.full_name || "");
    setWaitlistOpen(true);
  };

  const submitWaitlist = async () => {
    if (!wlEmail || !wlEmail.includes("@")) { toast.error(t("common.error")); return; }
    setWlBusy(true);
    try {
      const { data } = await api.post("/subscriptions/waitlist/france", {
        email: wlEmail,
        full_name: wlName,
        company_name: wlCompany,
        plan_slug: waitlistPlan,
        note: wlNote,
      });
      if (data.already_registered) toast.info(t("sub.fr.form_already"));
      else toast.success(t("sub.fr.form_success"));
      setWaitlistOpen(false);
      setWlCompany(""); setWlNote("");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setWlBusy(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12" data-testid="subscription-page">
      <button onClick={() => nav("/vendeur")} className="text-sm text-[#4B5563] hover:text-[#1A3322] mb-4 inline-flex items-center gap-1" data-testid="back-to-dashboard">
        <ArrowLeft className="w-4 h-4"/>{t("sub.back")}
      </button>

      <div className="mb-8">
        <span className="label-uppercase text-xs text-[#C85A32]">SUKANACOOP</span>
        <h1 className="heading font-extrabold text-3xl sm:text-4xl lg:text-5xl mt-2 text-[#111812]" data-testid="sub-title">{t("sub.title")}</h1>
        <p className="text-[#4B5563] mt-3 max-w-2xl">{t("sub.subtitle")}</p>
      </div>

      <div className="bg-gradient-to-r from-[#C85A32] to-[#D98A2C] text-white rounded-2xl p-5 mb-8 flex flex-wrap items-center gap-3" data-testid="sub-promo-banner">
        <Gift className="w-6 h-6 shrink-0"/>
        <div className="flex-1 min-w-[200px]">
          <p className="font-extrabold text-lg leading-tight">{t("sub.promo_title")}</p>
          <p className="text-sm opacity-95">{t("sub.promo_desc")}</p>
        </div>
      </div>

      {mine && (
        <div className="mb-8 bg-white border border-[#1A3322]/10 rounded-lg p-5 flex flex-wrap items-center gap-4" data-testid="current-plan-block">
          <div className="flex-1 min-w-[200px]">
            <p className="label-uppercase text-xs text-[#1A3322]">{t("sub.current_plan")}</p>
            <p className="heading font-extrabold text-2xl mt-1">{mine.plan.name}</p>
            <p className="text-sm text-[#4B5563] mt-1">
              {t("sub.commission")} <strong>{(mine.commission_rate * 100).toFixed(0)}%</strong> · {mine.listings_used}/{mine.listings_max === 999999 ? "∞" : mine.listings_max} {t("sub.listings_used")}
            </p>
            {mine.subscription?.status === "trialing" && (
              <p className="text-xs text-[#16a34a] mt-1 font-semibold" data-testid="sub-trial-badge">🎁 {t("sub.trial_active")}</p>
            )}
            {mine.subscription?.cancel_at_period_end && (
              <p className="text-xs text-[#C85A32] mt-1 font-semibold" data-testid="sub-cancel-notice">⚠️ {t("sub.will_cancel")}</p>
            )}
          </div>
          {mine.subscription && mine.subscription.status !== "canceled" && !mine.subscription.cancel_at_period_end && (
            <Button variant="outline" onClick={cancel} className="rounded-full" data-testid="cancel-sub-btn">{t("sub.cancel_btn")}</Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="plans-grid">
        {globalPlans.map((plan) => {
          const Icon = PLAN_ICONS[plan.slug] || Sparkles;
          const colors = PLAN_COLORS[plan.slug] || PLAN_COLORS.free;
          const isCurrent = currentSlug === plan.slug;
          const features = lang === "en" ? plan.features_en : plan.features_fr;
          const name = lang === "en" ? plan.name_en : plan.name;
          return (
            <div key={plan.slug}
                 className={`relative rounded-2xl border-2 p-6 ${colors.border} ${colors.bg} ${plan.slug === "standard" ? "ring-2 ring-[#C85A32]/20" : ""}`}
                 data-testid={`plan-card-${plan.slug}`}>
              {plan.slug === "standard" && (
                <span className="absolute -top-3 right-4 bg-[#C85A32] text-white text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full">
                  {t("sub.popular")}
                </span>
              )}
              <div className={`w-11 h-11 rounded-xl bg-white border ${colors.border} flex items-center justify-center mb-4`}>
                <Icon className={`w-5 h-5 ${colors.accent}`}/>
              </div>
              <h3 className="heading font-extrabold text-2xl text-[#111812]">{name}</h3>
              <div className="mt-2">
                <span className="heading font-extrabold text-4xl text-[#1A3322]">${plan.price_usd}</span>
                {plan.price_usd > 0 && <span className="text-sm text-[#4B5563] ml-1">/{t("sub.month")}</span>}
              </div>
              {plan.trial_days > 0 && (
                <p className="text-xs text-green-700 font-semibold mt-1">🎁 {plan.trial_days} {t("sub.trial_days")}</p>
              )}
              <ul className="space-y-2 mt-5 mb-6">
                {features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#111812]">
                    <Check className={`w-4 h-4 shrink-0 mt-0.5 ${colors.accent}`}/>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <Button disabled className="w-full pill-btn rounded-full opacity-70" data-testid={`plan-current-${plan.slug}`}>{t("sub.your_plan")}</Button>
              ) : plan.slug === "free" ? (
                <Button variant="outline" disabled className="w-full rounded-full opacity-70" data-testid={`plan-downgrade-${plan.slug}`}>
                  {mine?.subscription ? t("sub.downgrade_at_end") : t("sub.default_plan")}
                </Button>
              ) : (
                <Button
                  onClick={() => subscribe(plan.slug)}
                  disabled={busy === plan.slug}
                  className={`w-full pill-btn rounded-full ${plan.slug === "standard" ? "bg-[#C85A32] hover:bg-[#D98A2C]" : "bg-[#1A3322] hover:bg-[#24472F]"}`}
                  data-testid={`plan-subscribe-${plan.slug}`}
                >
                  {busy === plan.slug ? t("common.loading") : t("sub.choose_plan")}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* 🇫🇷 France plans (coming soon) */}
      {francePlans.length > 0 && (
        <div className="mt-16" data-testid="france-plans-section">
          <div className="bg-gradient-to-r from-blue-50 via-white to-red-50 border border-[#1A3322]/10 rounded-2xl p-6 mb-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="max-w-2xl">
                <h2 className="heading font-extrabold text-2xl sm:text-3xl text-[#111812]" data-testid="france-plans-title">
                  {t("sub.fr.section_title")}
                </h2>
                <p className="text-sm text-[#4B5563] mt-2">{t("sub.fr.section_desc")}</p>
                <p className="text-[11px] text-[#4B5563] mt-1 italic">{t("sub.fr.audit_note")}</p>
              </div>
              <Button onClick={() => openWaitlist("")} className="pill-btn bg-[#1A3322] hover:bg-[#24472F] rounded-full shrink-0" data-testid="france-notify-cta">
                {t("sub.fr.notify_cta")}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="france-plans-grid">
            {francePlans.map((plan) => {
              const Icon = PLAN_ICONS[plan.slug] || Sparkles;
              const colors = PLAN_COLORS[plan.slug] || PLAN_COLORS.free;
              const features = lang === "en" ? plan.features_en : plan.features_fr;
              const name = lang === "en" ? plan.name_en : plan.name;
              return (
                <div key={plan.slug}
                     className={`relative rounded-2xl border-2 p-6 ${colors.border} ${colors.bg} opacity-90`}
                     data-testid={`france-plan-card-${plan.slug}`}>
                  <span className="absolute -top-3 right-4 bg-[#1A3322] text-white text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full">
                    🇫🇷 {t("sub.fr.coming_soon")}
                  </span>
                  <div className={`w-11 h-11 rounded-xl bg-white border ${colors.border} flex items-center justify-center mb-4`}>
                    <Icon className={`w-5 h-5 ${colors.accent}`}/>
                  </div>
                  <h3 className="heading font-extrabold text-2xl text-[#111812]">{name}</h3>
                  <div className="mt-2">
                    <span className="heading font-extrabold text-4xl text-[#1A3322]">{plan.price_eur?.toFixed(2)} €</span>
                    <span className="text-sm text-[#4B5563] ml-1">/{t("sub.month")}</span>
                  </div>
                  <p className="text-xs text-[#4B5563] mt-1">
                    <span className="font-semibold">{(plan.commission_rate * 100).toFixed(0)}%</span> {t("sub.commission").toLowerCase()}
                  </p>
                  <ul className="space-y-2 mt-5 mb-6">
                    {features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#111812]">
                        <Check className={`w-4 h-4 shrink-0 mt-0.5 ${colors.accent}`}/>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => openWaitlist(plan.slug)}
                    variant="outline"
                    className="w-full rounded-full border-[#1A3322]/30 text-[#1A3322] hover:bg-[#F5F5F0]"
                    data-testid={`france-plan-notify-${plan.slug}`}
                  >
                    <Lock className="w-3.5 h-3.5 mr-1.5"/>{t("sub.fr.coming_soon")}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* France waitlist modal */}
      <Dialog open={waitlistOpen} onOpenChange={setWaitlistOpen}>
        <DialogContent className="max-w-md" data-testid="france-waitlist-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-[#C85A32]"/>{t("sub.fr.modal_title")}</DialogTitle>
            <DialogDescription>{t("sub.fr.modal_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t("sub.fr.form_email")} *</Label>
              <Input type="email" value={wlEmail} onChange={(e) => setWlEmail(e.target.value)} required data-testid="wl-email"/>
            </div>
            <div className="space-y-1">
              <Label>{t("sub.fr.form_name")}</Label>
              <Input value={wlName} onChange={(e) => setWlName(e.target.value)} data-testid="wl-name"/>
            </div>
            <div className="space-y-1">
              <Label>{t("sub.fr.form_company")}</Label>
              <Input value={wlCompany} onChange={(e) => setWlCompany(e.target.value)} data-testid="wl-company"/>
            </div>
            {waitlistPlan && (
              <div className="space-y-1">
                <Label>{t("sub.fr.form_plan")}</Label>
                <div className="px-3 py-2 rounded-md bg-[#F5F5F0] text-sm text-[#111812] font-semibold">
                  {(francePlans.find((p) => p.slug === waitlistPlan) || {}).name || waitlistPlan}
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label>{t("sub.fr.form_note")}</Label>
              <Textarea rows={3} value={wlNote} onChange={(e) => setWlNote(e.target.value)} data-testid="wl-note"/>
            </div>
            <Button onClick={submitWaitlist} disabled={wlBusy} className="w-full pill-btn bg-[#C85A32] hover:bg-[#D98A2C] rounded-full" data-testid="wl-submit">
              {wlBusy ? t("common.loading") : t("sub.fr.form_submit")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="mt-10 max-w-3xl text-sm text-[#4B5563] space-y-2" data-testid="sub-legal">
        <p><strong>{t("sub.faq_1_q")}</strong> {t("sub.faq_1_a")}</p>
        <p><strong>{t("sub.faq_2_q")}</strong> {t("sub.faq_2_a")}</p>
        <p><strong>{t("sub.faq_3_q")}</strong> {t("sub.faq_3_a")}</p>
      </div>
    </div>
  );
}
